import React, { useEffect, useState, useMemo, useCallback} from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceDot,     // ← add
  ReferenceLine,     // ← optional
  Label
} from 'recharts';
import { useDevice } from "./context/DeviceContext";
import { Sparklines, SparklinesLine } from "react-sparklines";

const PAGE_SIZE = 10;

const timeFrames = {
  '1M': 22,
  '6M': 132,
  '1Y': 252,
  '3Y': 252 * 3,
  '5Y': 252 * 5,
  '10Y': 252 * 10,
  'Max': Infinity
};

const formatIndustryName = (str) => {
  if (!str) return '';
  return str.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
};


const PriceChart = React.memo(function PriceChart({
  chartData,
  stockInfo,
  isMobile,
  margin,
  xTicks,
  volumeTicks,
  priceTicks,
  yMin,
  yMax,
  decimalPoints,
  tickPrefix,
  newsData,
  onDotClick,
  getCloseByDate,
  activeDotKey,
  eventKey,
}) {
  const EVENT_LABELS = {
    news: "N",
    dividend: "D",
    result: "R",
    split: "S",
    default: "•",
  };

  // ✅ Toggle ATS line
  const [showATS, setShowATS] = React.useState(false);

  // ✅ Toggle OI line
  const [showOI, setShowOI] = React.useState(false);

  // ✅ Toggle Options PCR mini chart
  const [showPCR, setShowPCR] = React.useState(false);

  // Delivery stack check
  const hasDeliveryStack = chartData?.some(
    (d) => d.deliveredVol != null && d.undeliveredVol != null
  );

  // ✅ Detect if ATS is possible (needs trades > 0)
  const hasTrades = chartData?.some((d) => Number(d?.trades) > 0);

  const hasOI = chartData?.some(
    (d) => d?.oi != null && Number.isFinite(Number(d.oi))
  );

  const hasOptionsOI = chartData?.some(
    (d) =>
      d?.ceOi != null && Number.isFinite(Number(d.ceOi)) &&
      d?.peOi != null && Number.isFinite(Number(d.peOi))
  );

  // ✅ Add ATS into data (non-mutating)
  const dataWithATS = React.useMemo(() => {
    if (!Array.isArray(chartData) || chartData.length === 0) return chartData;

    return chartData.map((d) => {
      const vol = Number(d.volume ?? 0);
      const tr = Number(d.trades ?? 0);
      const ats = tr > 0 && Number.isFinite(vol) ? vol / tr : null;
      return { ...d, ats };
    });
  }, [chartData]);

  // ✅ Build aligned options flow data (force 0s to avoid “alternate” look)
  const optFlowData = React.useMemo(() => {
    if (!Array.isArray(dataWithATS) || dataWithATS.length === 0) return [];

    return dataWithATS.map((d) => {
      const ce = Number(d?.ceOi);
      const pe = Number(d?.peOi);

      const ceOk = Number.isFinite(ce) ? ce : 0;
      const peOk = Number.isFinite(pe) ? pe : 0;

      const pcr = Number(d?.pcrOi);
      const pcrOk = Number.isFinite(pcr) ? pcr : null; // keep line breakable

      return {
        date: d.date,         // IMPORTANT: same date field as main chart
        peBar:  peOk,          // ✅ PUTS on top (positive)
        ceBar: -ceOk,          // ✅ CALLS on bottom (negative)
        pcrOi: pcrOk,         // currently (PE/CE)-1
      };
    });
  }, [dataWithATS]);

  const OI_color = "#d2bf34ff";
  const ATS_color = "#f97316";

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const closeEntry = payload.find((it) => it?.dataKey === "close");
    const p = (closeEntry?.payload || payload[0]?.payload) ?? {};

    const close = Number(p.close);
    const vol = Number(p.volume ?? 0);

    const delRaw = p.delivery;
    const del = delRaw == null ? null : Number(delRaw);

    const tradesRaw = p.trades;
    const trades = tradesRaw == null ? null : Number(tradesRaw);

    const ats = p.ats;
    const showATSVal =
      ats != null && Number.isFinite(ats) && trades != null && trades > 0;

    const showDel =
      del != null && Number.isFinite(del) && Number.isFinite(vol) && vol > 0;

    const delPct = showDel ? (del / vol) * 100 : null;

    // ✅ OI%
    const oiRaw = p.oi;
    const oi = oiRaw == null ? null : Number(oiRaw);
    const showOIVal = oi != null && Number.isFinite(oi);

    const fmtInt = (n) =>
      Number.isFinite(n) ? n.toLocaleString("en-IN") : "-";

    return (
      <div className="rounded-md border bg-white px-2.5 py-2 text-xs shadow">
        <div className="text-[11px] text-gray-500">{label}</div>

        <div className="mt-0.5 flex items-baseline gap-1">
          <span className="text-gray-500">Close:</span>
          <span className="font-semibold text-gray-900">
            {Number.isFinite(close) ? close.toFixed(2) : "-"}
          </span>
        </div>

        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-gray-600">Vol:</span>
          <span className="text-gray-900">{fmtInt(vol)}</span>

          <span className="ml-1 text-gray-600">D:</span>
          <span className="text-gray-900">
            {showDel ? delPct.toFixed(1) + "%" : "-"}
          </span>
        </div>

        <div className="mt-1 text-gray-500">
          <span className="text-gray-600">Vol/Trade:</span>{" "}
          <span className="font-medium text-gray-900">
            {showATSVal ? ats.toFixed(0) : "-"}
          </span>
        </div>

        {showOIVal && (
          <div className="mt-1 text-gray-500">
            <span className="text-gray-600">OI%:</span>{" "}
            <span className="font-medium text-gray-900">
              {oi.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    );
  };

  const OptionsTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const ceItem = payload.find((p) => p?.dataKey === "ceBar");
    const peItem = payload.find((p) => p?.dataKey === "peBar");
    const pcrItem = payload.find((p) => p?.dataKey === "pcrOi");

    const ceBar = ceItem?.value;
    const peBar = peItem?.value;
    const pcrNorm = pcrItem?.value;

    const ce = Number.isFinite(Number(ceBar)) ? Math.abs(Number(ceBar)) : null;
    const pe = Number.isFinite(Number(peBar)) ? Math.abs(Number(peBar)) : null;

    // You are plotting pcrOi = (PE/CE)-1, so raw PCR = pcrNorm + 1
    const rawPCR =
      pcrNorm != null && Number.isFinite(Number(pcrNorm)) ? Number(pcrNorm) + 1 : null;

    const fmtInt = (n) => (Number.isFinite(n) ? n.toLocaleString("en-IN") : "-");
    const fmtPcr = (n) => (Number.isFinite(n) ? n.toFixed(2) : "-");

    const bias =
      rawPCR == null
        ? ""
        : rawPCR > 1
        ? " (Put > Call)"
        : rawPCR < 1
        ? " (Call > Put)"
        : " (Neutral)";

    return (
      <div className="rounded-md border bg-white px-2.5 py-2 text-xs shadow">
        <div className="text-[11px] text-gray-500">{label}</div>

        <div className="mt-1 flex items-baseline justify-between gap-3">
          <span className="text-gray-600">PE OI:</span>
          <span className="font-medium text-gray-900">{fmtInt(pe)}</span>
        </div>

        <div className="mt-1 flex items-baseline justify-between gap-3">
          <span className="text-gray-600">CE OI:</span>
          <span className="font-medium text-gray-900">{fmtInt(ce)}</span>
        </div>

        <div className="mt-1 flex items-baseline justify-between gap-3">
          <span className="text-gray-600">PCR:</span>
          <span className="font-semibold text-gray-900">
            {fmtPcr(rawPCR)}
            <span className="font-normal text-gray-500">{bias}</span>
          </span>
        </div>
      </div>
    );
  };

  // ✅ Consistent x ticks strategy for BOTH charts
  const alignedXTicks = isMobile ? xTicks.filter((_, i) => i % 2 === 0) : xTicks;

  // ✅ Fix plot width jitter by fixing axis widths
  const LEFT_AXIS_W = isMobile ? 44 : 54;
  const RIGHT_AXIS_W = isMobile ? 44 : 54;

  const pcrAbsMax = React.useMemo(() => {
    let m = 0;
    for (const d of optFlowData) {
      const v = Number(d?.pcrOi);
      if (Number.isFinite(v)) m = Math.max(m, Math.abs(v));
    }
    // fallback so axis never collapses
    return m > 0 ? m : 1;
  }, [optFlowData]);

  const flowAbsMax = React.useMemo(() => {
    if (!Array.isArray(optFlowData) || optFlowData.length === 0) return 0;

    let max = 0;
    for (const d of optFlowData) {
      const ce = Math.abs(Number(d?.ceBar) || 0);
      const pe = Math.abs(Number(d?.peBar) || 0);
      max = Math.max(max, ce, pe);
    }

    return max;
  }, [optFlowData]);
    
  return (
    <div className="-mx-4 sm:mx-0">
      {/* MAIN CHART */}
      <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
        <ComposedChart
          data={dataWithATS}
          margin={margin}
          tabIndex={-1}
          focusable={false}
          syncId="priceSync"
        >
          <CartesianGrid stroke="#eae8e0ff" horizontal vertical={false} yAxisId="right" />

          <XAxis
            dataKey="date"
            ticks={alignedXTicks}
            tick={{ fontSize: isMobile ? 8 : 10, fill: "#4f5763" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
          />

          <YAxis
            yAxisId="left"
            orientation="left"
            width={LEFT_AXIS_W}
            ticks={volumeTicks}
            tick={{ fontSize: isMobile ? 8 : 10, fill: "#4f5763" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
            label={
              !isMobile
                ? { value: "Volume", angle: -90, position: "insideLeft", fill: "#4f5763" }
                : undefined
            }
            tickFormatter={(value) => {
              if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
              if (value >= 1_000) return (value / 1_000).toFixed(0) + "K";
              return value;
            }}
          />

          <YAxis
            yAxisId="right"
            orientation="right"
            width={RIGHT_AXIS_W}
            ticks={priceTicks}
            tick={{ fontSize: isMobile ? 8 : 10, fill: "#4f5763" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
            label={
              !isMobile
                ? { value: "Price", angle: 90, position: "insideRight", fill: "#4f5763" }
                : undefined
            }
            tickFormatter={(value) => value.toFixed(decimalPoints)}
          />

          <YAxis yAxisId="ats" width={0} orientation="right" hide domain={["auto", "auto"]} />
          <YAxis yAxisId="oi" width={0} orientation="right" hide domain={["auto", "auto"]} />

          <Tooltip content={<CustomTooltip />} />

          {hasDeliveryStack ? (
            <>
              <Bar
                yAxisId="left"
                dataKey="deliveredVol"
                stackId="vol"
                barSize={isMobile ? 10 : 20}
                fill="#87a1d6ff"
                isAnimationActive={false}
              />
              <Bar
                yAxisId="left"
                dataKey="undeliveredVol"
                stackId="vol"
                barSize={isMobile ? 10 : 20}
                fill="#BDD2FD"
                isAnimationActive={false}
              />
            </>
          ) : (
            <Bar
              yAxisId="left"
              dataKey="volume"
              barSize={isMobile ? 10 : 20}
              fill="#BDD2FD"
              isAnimationActive={false}
            />
          )}

          <Line
            yAxisId="right"
            type="monotone"
            dataKey="close"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />

          {showATS && hasTrades && (
            <Line
              yAxisId="ats"
              type="monotone"
              dataKey="ats"
              stroke={ATS_color}
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          )}

          {showOI && hasOI && (
            <Line
              yAxisId="oi"
              type="monotone"
              dataKey="oi"
              stroke={OI_color}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          )}

          {newsData.map((ev) => {
            const match = getCloseByDate(dataWithATS, ev.date);
            if (!match) return null;

            const { date: xDate, close: y } = match;
            const key = eventKey(ev);
            const isActive = key === activeDotKey;

            return (
              <ReferenceDot
                key={key}
                x={xDate}
                y={y}
                yAxisId="right"
                r={12}
                isFront={isActive}
                isAnimationActive={false}
                onClick={() =>
                  onDotClick({
                    type: ev.type,
                    date: xDate,
                    text: ev.details,
                    url: ev.url,
                    price: y,
                  })
                }
                shape={(props) => {
                  const { cx, cy, onClick } = props;
                  const offsetY = -10;
                  const r = 12;

                  return (
                    <g
                      transform={`translate(${cx}, ${cy + offsetY})`}
                      style={{ cursor: "pointer" }}
                      onClick={onClick}
                    >
                      <circle r={20} fill="transparent" style={{ pointerEvents: "all" }} />
                      <circle
                        r={r}
                        fill={isActive ? "#ef4444" : "#f59e0b"}
                        stroke={isActive ? "#111827" : "white"}
                        strokeWidth={isActive ? 3 : 2}
                      />
                      <text
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={isActive ? 12 : 10}
                        fill="white"
                      >
                        {EVENT_LABELS[ev.type] || EVENT_LABELS.default}
                      </text>
                    </g>
                  );
                }}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>

      {/* MINI OPTIONS PANEL */}
      {showPCR && hasOptionsOI && (
      <ResponsiveContainer width="100%" height={isMobile ? 140 : 140}>
        <ComposedChart
          data={optFlowData}
          margin={margin}          // ✅ same margins
          barCategoryGap="0%"
          barGap={0}
          stackOffset="sign"
          syncId="priceSync"       // ✅ sync with main chart
        >
          <CartesianGrid stroke="#eae8e0ff" vertical={false} />

          <XAxis
            dataKey="date"
            ticks={alignedXTicks}   // ✅ same ticks
            tick={{ fontSize: isMobile ? 10 : 8, fill: "#4f5763" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
          />

          <YAxis
            yAxisId="flow"
            orientation="left"
            width={LEFT_AXIS_W}     // ✅ same left gutter
            domain={[-flowAbsMax, flowAbsMax]}   // ✅ CRITICAL FIX
            tick={{ fontSize: isMobile ? 8 : 8, fill: "#4f5763" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
            tickFormatter={(v) => {
              const x = Math.abs(Number(v));
              if (x >= 1_000_000) return (x / 1_000_000).toFixed(1) + "M";
              if (x >= 1_000) return (x / 1_000).toFixed(0) + "K";
              return String(x);
            }}
          />

          {/* Top-left label: PE */}
          <ReferenceLine yAxisId="flow" y={flowAbsMax} stroke="transparent">
            <Label
              value="PE OI Volume"
              position="insideTopLeft"
              offset={4}
              fill="#E58F8C"      // or match your PE bar color
              fontSize={isMobile ? 10 : 11}
              fontWeight={600}
            />
          </ReferenceLine>

          {/* Bottom-left label: CE */}
          <ReferenceLine yAxisId="flow" y={-flowAbsMax} stroke="transparent">
            <Label
              value="CE OI Volume"
              position="insideBottomLeft"
              offset={4}
              fill="#0f766e"        // or match your CE bar color
              fontSize={isMobile ? 10 : 11}
              fontWeight={600}
            />
          </ReferenceLine>

          {/* ✅ Hidden PCR scale axis */}
          <YAxis
            yAxisId="pcr"
            orientation="right"
            hide
            domain={[-pcrAbsMax, pcrAbsMax]}   // ✅ always includes 0, always stable
            allowDataOverflow={true}
          />

          {/* ✅ THIS is the alignment fix: reserve the right-side gutter always */}
          <YAxis
            yAxisId="gutter"
            orientation="right"
            width={RIGHT_AXIS_W}
            tick={false}
            axisLine={false}
            tickLine={false}
          />

          <ReferenceLine yAxisId="flow" y={0} stroke="#111827" strokeWidth={1} />
          <Tooltip content={<OptionsTooltip />} />

          <Bar
            yAxisId="flow"
            dataKey="peBar"
            stackId="pc"
            fill="#E58F8C"
            barSize={isMobile ? 10 : 20}
            isAnimationActive={false}
          />

          <Bar
            yAxisId="flow"
            dataKey="ceBar"
            stackId="pc"
            fill="#73BDB6"
            barSize={isMobile ? 10 : 20}
            isAnimationActive={false}
          />

          <Line
            yAxisId="pcr"
            type="monotone"
            dataKey="pcrOi"
            stroke="#111827"
            strokeWidth={1.5}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      )}

      {stockInfo && (
        <div className="flex justify-center items-center py-1">
          <h4 className="text-sm font-bold text-center">
            {stockInfo.quoteType === "SECTOR"
              ? formatIndustryName(stockInfo.longName)
              : stockInfo.longName}
          </h4>
        </div>
      )}

      {/* ✅ Toggles */}
      <div className="mt-2 overflow-x-auto">
        <div
          className="
            mx-auto
            flex
            w-fit
            gap-3
            whitespace-nowrap
            px-2
            sm:justify-center
          "
        >
          {hasTrades && (
            <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={showATS}
                onChange={() => setShowATS((v) => !v)}
                className="h-4 w-4 rounded border-gray-400"
                style={{ accentColor: ATS_color }}
              />
              <span>
                <span className="font-medium">ATS</span>
                <span className="hidden sm:inline"> (Volume/Trades)</span>
              </span>
            </label>
          )}

          {hasOI && (
            <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={showOI}
                onChange={() => setShowOI((v) => !v)}
                className="h-4 w-4 rounded border-gray-400"
                style={{ accentColor: OI_color }}
              />
              <span>
                <span className="font-medium">Fut OI%</span>
                <span className="hidden sm:inline"> (FutEq/MWPL)</span>
              </span>
            </label>
          )}

          {hasOptionsOI && (
            <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={showPCR}
                onChange={() => setShowPCR((v) => !v)}
                className="h-4 w-4 rounded border-gray-400"
                style={{ accentColor: "#111827" }}
              />
              <span>
                <span className="font-medium">Options PCR</span>
                <span className="hidden sm:inline"> (CE/PE OI)</span>
              </span>
            </label>
          )}
        </div>
      </div>

    </div>
  );

});



/**
 * Usage:
 * <NewsFeedTable data={newsFeedData} />
 */
const NewsFeedTable = ({ data = [] }) => {
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(null); // "1D" | "1W" | null
  const [sortDir, setSortDir] = useState("desc");   // "asc" | "desc"

  const siteName = (linkOrHost) => {
    if (!linkOrHost) return "unknown";
    try {
      const host = linkOrHost.includes("://")
        ? new URL(linkOrHost).hostname
        : linkOrHost;
      const cleaned = host.replace(/^www\./i, "");
      return cleaned.split(".")[0] || cleaned;
    } catch {
      const cleaned = String(linkOrHost)
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "");
      return cleaned.split(".")[0] || "unknown";
    }
  };

  const formatDate = (d) => {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d || "");
    return dt.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const sorted = useMemo(() => {
    let items = [...(data || [])];
    if (sortField === "1D") {
      items.sort((a, b) => {
        const ca = parseFloat(a?.change_1D ?? 0);
        const cb = parseFloat(b?.change_1D ?? 0);
        return sortDir === "asc" ? ca - cb : cb - ca;
      });
    } else if (sortField === "1W") {
      items.sort((a, b) => {
        const ca = parseFloat(a?.change_1W ?? 0);
        const cb = parseFloat(b?.change_1W ?? 0);
        return sortDir === "asc" ? ca - cb : cb - ca;
      });
    } else {
      // default sort by published desc
      items.sort((a, b) => {
        const ta = new Date(a?.published || 0).getTime();
        const tb = new Date(b?.published || 0).getTime();
        return (isNaN(tb) ? 0 : tb) - (isNaN(ta) ? 0 : ta);
      });
    }
    return items;
  }, [data, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const pageSlice = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [sorted, pageSafe]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const toggleSort = (field) => {
    if (sortField === field) {
      // toggle direction
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      // new field defaults to desc
      setSortField(field);
      setSortDir("desc");
    }
    setPage(1);
  };

  return (
    <div className="w-full mt-6">
      <h4 className="text-lg font-semibold">News & Stories</h4>

      {/* Top controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="text-sm text-gray-500">
          Showing {pageSlice.length} of {sorted.length} items
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => toggleSort("1D")}
            className="px-1.5 py-1.5 rounded-md border text-sm"
          >
            1D%{" "}
            {sortField === "1D" ? (sortDir === "asc" ? "↑" : "↓") : ""}
          </button>
          <button
            onClick={() => toggleSort("1W")}
            className="px-1.5 py-1.5 rounded-md border text-sm"
          >
            1W%{" "}
            {sortField === "1W" ? (sortDir === "asc" ? "↑" : "↓") : ""}
          </button>
          <button
            onClick={goPrev}
            disabled={pageSafe === 1}
            className="px-3 py-1.5 rounded-md border text-sm disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-sm tabular-nums">
            Page {pageSafe} of {totalPages}
          </span>
          <button
            onClick={goNext}
            disabled={pageSafe === totalPages}
            className="px-3 py-1.5 rounded-md border text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Table wrapper */}
      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm">
          <tbody className="divide-y">
            {pageSlice.length === 0 ? (
              <tr>
                <td colSpan={2} className="px-3 py-6 text-center text-gray-500">
                  No news found.
                </td>
              </tr>
            ) : (
              pageSlice.map((row, idx) => {
                const title = row?.title || "(No title)";
                const link = row?.link || "#";
                const sourceLabel = siteName(row?.source || row?.link || "");
                const published = formatDate(row?.published);

                const change_1D = parseFloat(row?.change_1D ?? null);
                const change_1W = parseFloat(row?.change_1W ?? null);

                const changeText_1D =
                  change_1D != null && !isNaN(change_1D)
                    ? `1D ${change_1D.toFixed(2)}%`
                    : "";

                const changeText_1W =
                  change_1W != null && !isNaN(change_1W)
                    ? `1W ${change_1W.toFixed(2)}%`
                    : "";

                const changeClass_1D =
                  change_1D != null && !isNaN(change_1D)
                    ? change_1D >= 0
                      ? "text-green-600"
                      : "text-red-600"
                    : "text-gray-500";

                const changeClass_1W =
                  change_1W != null && !isNaN(change_1W)
                    ? change_1W >= 0
                      ? "text-green-600"
                      : "text-red-600"
                    : "text-gray-500";

                return (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-100"}
                  >
                    <td className="px-3 py-2">
                      <span className="font-light hover:decoration-solid break-words">
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={title}
                        >
                          {title}
                        </a>
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        <a
                          href={row.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline"
                        >
                          {sourceLabel}
                        </a>{" "}
                        ({published})
                      </span>
                      {changeText_1D && (
                        <span className={`text-xs ml-2 ${changeClass_1D}`}>
                          {changeText_1D}
                        </span>
                      )}
                      {changeText_1W && (
                        <span className={`text-xs ml-2 ${changeClass_1W}`}>
                          {changeText_1W}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FinancialImpacts = ({ stockInfo, className = "" }) => {
  if (!stockInfo) return null;

  const parseList = (str = "", delim = "*") =>
    String(str)
      .split(delim)
      .map(s => s.trim())
      .filter(Boolean);

  const positives = parseList(stockInfo.positiveImpacts || "", "*");
  const negatives = parseList(stockInfo.negativeImpacts || "", "/");

  return (
    <div className={`mt-6 ${className}`}>
      {/* Header with top margin */}
      <h4 className="text-lg font-semibold mb-2">
        Impacts
      </h4>

      {/* Card */}
      <div className="rounded-xl border border-gray-200 bg-white">
        {/* Two-column layout (stacks on mobile) */}
        <div className="grid grid-cols-1 md:grid-cols-2 items-stretch">
          {/* Positive column */}
          <div className="p-4 flex flex-col h-full border-b border-gray-200 md:border-b-0 md:border-r">
            <div className="text-sm font-semibold text-gray-700 inline-flex items-center gap-2">
              <span aria-hidden>👍</span>
              <span>Positive Impacts</span>
            </div>

            {positives.length ? (
              <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-gray-800">
                {positives.map((item, idx) => (
                  <li key={idx} className="break-words">{item}</li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-gray-400 italic">
                No positive impacts available
              </p>
            )}
          </div>

          {/* Negative column */}
          <div className="p-4 flex flex-col h-full">
            <div className="text-sm font-semibold text-gray-700 inline-flex items-center gap-2">
              <span aria-hidden>👎</span>
              <span>Negative Impacts</span>
            </div>

            {negatives.length ? (
              <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-gray-800">
                {negatives.map((item, idx) => (
                  <li key={idx} className="break-words">{item}</li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-gray-400 italic">
                No negative impacts available
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StockEvents = ({ data = [] }) => {
  // ✅ Hooks always first; no early returns before hooks
  const [sortField, setSortField] = useState("Date"); // "Date" | "1D" | "1W"
  const [sortDir, setSortDir] = useState("desc");     // "asc" | "desc"

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sortedEvents = useMemo(() => {
    const arr = [...(data || [])];
    if (sortField === "1D") {
      arr.sort((a, b) => {
        const av = parseFloat(a?.change_1D ?? 0);
        const bv = parseFloat(b?.change_1D ?? 0);
        return sortDir === "asc" ? av - bv : bv - av;
      });
    } else if (sortField === "1W") {
      arr.sort((a, b) => {
        const av = parseFloat(a?.change_1W ?? 0);
        const bv = parseFloat(b?.change_1W ?? 0);
        return sortDir === "asc" ? av - bv : bv - av;
      });
    } else {
      // Date
      arr.sort((a, b) => {
        const ta = new Date(a?.date || 0).getTime();
        const tb = new Date(b?.date || 0).getTime();
        const diff = (isNaN(tb) ? 0 : tb) - (isNaN(ta) ? 0 : ta);
        return sortDir === "asc" ? -diff : diff;
      });
    }
    return arr;
  }, [data, sortField, sortDir]);

  const pctText = (v) =>
    v == null || isNaN(v) ? "—" : `${Number(v).toFixed(2)}%`;
  const pctClass = (v) =>
    v == null || isNaN(v)
      ? "text-gray-500"
      : Number(v) >= 0
      ? "text-green-600"
      : "text-red-600";
  const sortCaret = (field) =>
    sortField === field ? (sortDir === "asc" ? "↑" : "↓") : "";

  return (
    <div className="w-full mt-6">
      <h4 className="text-lg font-semibold mb-2">Events</h4>

      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th
                className="px-4 py-2 text-left border border-gray-200 cursor-pointer select-none"
                onClick={() => toggleSort("Date")}
                title="Sort by Date"
              >
                Date {sortCaret("Date")}
              </th>
              <th className="px-4 py-2 text-left border border-gray-200">Purpose</th>
              <th
                className="px-4 py-2 text-left border border-gray-200 cursor-pointer select-none"
                onClick={() => toggleSort("1D")}
                title="Sort by 1D change"
              >
                1D {sortCaret("1D")}
              </th>
              <th
                className="px-4 py-2 text-left border border-gray-200 cursor-pointer select-none"
                onClick={() => toggleSort("1W")}
                title="Sort by 1W change"
              >
                1W {sortCaret("1W")}
              </th>
            </tr>
          </thead>

          <tbody>
            {(!data || data.length === 0) ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                  No events available.
                </td>
              </tr>
            ) : (
              sortedEvents.map((event, idx) => {
                const d = new Date(event.date);
                const dateStr = isNaN(d) ? String(event.date ?? "") : d.toLocaleDateString("en-IN");
                const c1d = event.change_1D;
                const c1w = event.change_1W;

                return (
                  <tr
                    key={event.hash ?? `${event.symbol}-${event.date}-${idx}`}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-4 py-2 border border-gray-200">{dateStr}</td>
                    <td className="px-4 py-2 border border-gray-200">{event.purpose || "—"}</td>
                    <td className={`px-4 py-2 border border-gray-200 tabular-nums ${pctClass(c1d)}`}>
                      {pctText(c1d)}
                    </td>
                    <td className={`px-4 py-2 border border-gray-200 tabular-nums ${pctClass(c1w)}`}>
                      {pctText(c1w)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StockAnnouncements = ({ data = [] }) => {
  // ✅ Hooks always first; no early returns before hooks
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(null); // "1D" | "1W" | null
  const [sortDir, setSortDir] = useState("desc");   // "asc" | "desc"

  // Safe data
  const rows = Array.isArray(data) ? data : [];

  // Helpers (no hooks)
  const siteName = (url) => {
    if (!url) return "link";
    try {
      const host = new URL(url).hostname.replace(/^www\./i, "");
      return host.split(".")[0] || host;
    } catch {
      return "link";
    }
  };

  // "25 Sept 2025"
  const formatPrettyDate = (d) => {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d || "");
    return dt.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const toNum = (v) => {
    if (v == null) return null;          // null / undefined
    if (typeof v === "string" && v.trim() === "") return null; // ""
    const n = Number(v);
    return Number.isFinite(n) ? n : null; // NaN / Infinity -> null
  };

  const pctText = (v) =>
    v == null || isNaN(v) ? "" : `${Number(v).toFixed(2)}%`;
  const pctClass = (v) =>
    v == null || isNaN(v) ? "text-gray-500" : Number(v) >= 0 ? "text-green-600" : "text-red-600";

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDir("desc");
    }
    setPage(1);
  };
  const caret = (field) => (sortField === field ? (sortDir === "asc" ? "↑" : "↓") : "");

  // Sort all, then paginate
  const sorted = useMemo(() => {
    const arr = [...rows];

    const cmpMissingLast = (av, bv) => {
      const aMissing = av == null;
      const bMissing = bv == null;
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;   // a goes AFTER b
      if (bMissing) return -1;  // b goes AFTER a
      return sortDir === "asc" ? av - bv : bv - av;
    };

    if (sortField === "1D") {
      arr.sort((a, b) => {
        const av = toNum(a?.change_1D ?? a?.chang);
        const bv = toNum(b?.change_1D ?? b?.chang);
        return cmpMissingLast(av, bv);
      });
    } else if (sortField === "1W") {
      arr.sort((a, b) => {
        const av = toNum(a?.change_1W);
        const bv = toNum(b?.change_1W);
        return cmpMissingLast(av, bv);
      });
    } else {
      arr.sort((a, b) => {
        const ta = new Date(a?.an_dt || a?.date || 0).getTime();
        const tb = new Date(b?.an_dt || b?.date || 0).getTime();
        return (isNaN(tb) ? 0 : tb) - (isNaN(ta) ? 0 : ta);
      });
    }

    return arr;
  }, [rows, sortField, sortDir]);


  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const start = (pageSafe - 1) * PAGE_SIZE;
  const pageSlice = sorted.slice(start, start + PAGE_SIZE);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="w-full mt-6">
      <h4 className="text-lg font-semibold">Announcements</h4>

      {/* Top controls (paging + sort) */}
      <div className="flex flex-wrap items-center justify-between gap-2 mt-2 mb-2">
        <div className="text-sm text-gray-500">
          Showing {pageSlice.length} of {sorted.length} items
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => toggleSort("1D")}
            className="px-1.5 py-1.5 rounded-md border text-sm"
            title="Sort by 1D change"
          >
            1D% {caret("1D")}
          </button>
          <button
            onClick={() => toggleSort("1W")}
            className="px-1.5 py-1.5 rounded-md border text-sm"
            title="Sort by 1W change"
          >
            1W% {caret("1W")}
          </button>
          <button
            onClick={goPrev}
            disabled={pageSafe === 1}
            className="px-3 py-1.5 rounded-md border text-sm disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-sm tabular-nums">
            Page {pageSafe} of {totalPages}
          </span>
          <button
            onClick={goNext}
            disabled={pageSafe === totalPages}
            className="px-3 py-1.5 rounded-md border text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* List-style rows in a bordered, rounded table for alignment/scroll */}
      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm">
          <tbody className="divide-y">
            {pageSlice.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500">No announcements found.</td>
              </tr>
            ) : (
              pageSlice.map((row, idx) => {
                const type = row?.desc || row?.purpose || "—";
                const description = row?.attchmntText || row?.bm_desc || "";
                const href = row?.attchmntFile || row?.link || null;
                const dateRaw = row?.an_dt || row?.date || null;
                const dateStr = formatPrettyDate(dateRaw);

                const c1d = parseFloat(row?.change_1D ?? row?.chang ?? null);
                const c1w = parseFloat(row?.change_1W ?? null);

                return (
                  <tr
                    key={row.hash ?? `${row.symbol}-${dateRaw}-${idx}`}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-4 py-3 border">
                      {/* {Type} – {Description} – {link} – {date} – (1D …) (1W …) */}
                      <div className="space-x-1">
                        <span className="font-medium">{type}</span>
                        {description && <span className="font-light text-gray-500">– {description}</span>}
                        {href && (
                          <>
                            <span>–</span>
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline"
                              title={href}
                            >
                              {siteName(href)}
                            </a>
                          </>
                        )}
                        {dateStr && (
                          <>
                            <span>–</span>
                            <span className="text-gray-700">{dateStr}</span>
                          </>
                        )}
                        {Number.isFinite(c1d) && (
                          <span className={`ml-2 ${pctClass(c1d)}`}>{`1D ${pctText(c1d)}`}</span>
                        )}
                        {Number.isFinite(c1w) && (
                          <span className={`ml-2 ${pctClass(c1w)}`}>{`1W ${pctText(c1w)}`}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Empty state AFTER hooks—no early return */}
      {rows.length === 0 && (
        <p className="mt-3 text-gray-500">No announcements available.</p>
      )}
    </div>
  );
};

const StockTrades = ({ symbol }) => {

  const TAB_CONFIG = [
    { key: "stockBulkDeals",   label: "Bulk" },
    { key: "stockBlockDeals",  label: "Block" },
    { key: "stockSastDeals",   label: "SAST" },
    { key: "stockInsiderDeals",label: "Insider" },
  ];

  /* ---- Tiny helpers (no per-tab schema) ---- */
  const fmtNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString("en-IN") : "—";
  };

  const fmtPrice = (v) => {
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "—";
  };

  // 25 Sept 2025 (uniform)
  const fmtDate = (d) => {
    const dt = new Date(d);
    return isNaN(dt) ? "—" : dt.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const pctTxt = (v) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(2)}%`);
  const pctCls = (v) =>
    v == null || isNaN(v) ? "text-gray-500" : Number(v) >= 0 ? "text-green-600" : "text-red-600";

  // Resolve fields that vary across tabs (simple OR-chains)
  const getClient = (r) => r.clientName || r.acquirerName || r.acqName || r.personName || "—";
  const getSide   = (r) => r.buySell || r.acqSaleType || r.transactionType || "—";
  const getMode   = (r) => r.acqMode || r.acquisitionMode || r.personCategory || r.relation || "—";
  const getQty    = (r) => r.qty || r.noOfShareAcq || r.noOfShareSale || r.secAcq || null;
  const getAvg    = (r) => r.watp || r.avgPrice || r.price || null;
  const getDealCr = (r) => (r.dealValue != null ? Number(r.dealValue) : null); // already in Cr if you set it so
  const getD1     = (r) => parseFloat(r.change_1D ?? r.change ?? r.chg ?? NaN);
  const getW1     = (r) => parseFloat(r.change_1W ?? NaN);

  const [data, setData] = useState({
    stockBulkDeals:   { data: [], loading: true, error: null },
    stockBlockDeals:  { data: [], loading: true, error: null },
    stockSastDeals:   { data: [], loading: true, error: null },
    stockInsiderDeals:{ data: [], loading: true, error: null },
  });
  const [activeKey, setActiveKey] = useState(TAB_CONFIG[0].key);

  // sorting (just 3 keys)
  const [sortKey, setSortKey] = useState("date"); // 'date' | 'd1' | 'w1'
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'

  useEffect(() => {
    let cancelled = false;

    // reset
    setData({
      stockBulkDeals:   { data: [], loading: true, error: null },
      stockBlockDeals:  { data: [], loading: true, error: null },
      stockSastDeals:   { data: [], loading: true, error: null },
      stockInsiderDeals:{ data: [], loading: true, error: null },
    });

    TAB_CONFIG.forEach(({ key }) => {
      axios
        .get(`${process.env.REACT_APP_API_URL}/api_2/${key}/${symbol}`)
        .then((res) => {
          if (cancelled) return;
          setData((prev) => ({
            ...prev,
            [key]: { data: Array.isArray(res.data) ? res.data : [], loading: false, error: null },
          }));
        })
        .catch((err) => {
          if (cancelled) return;
          console.error(`Error fetching ${key}:`, err);
          setData((prev) => ({
            ...prev,
            [key]: { data: [], loading: false, error: "Failed to load" },
          }));
        });
    });

    return () => { cancelled = true; };
  }, [symbol]);

  const active = data[activeKey] || { data: [], loading: false, error: null };

  const sortedRows = useMemo(() => {
    const arr = [...(active.data || [])];

    if (sortKey === "date") {
      arr.sort((a, b) => {
        const ta = new Date(a?.date || 0).getTime();
        const tb = new Date(b?.date || 0).getTime();
        const diff = (isNaN(tb) ? 0 : tb) - (isNaN(ta) ? 0 : ta);
        return sortDir === "asc" ? -diff : diff; // default desc
      });
    } else if (sortKey === "d1") {
      arr.sort((a, b) => {
        const av = getD1(a); const bv = getD1(b);
        const na = isNaN(av) ? -Infinity : av;
        const nb = isNaN(bv) ? -Infinity : bv;
        return sortDir === "asc" ? na - nb : nb - na;
      });
    } else if (sortKey === "w1") {
      arr.sort((a, b) => {
        const av = getW1(a); const bv = getW1(b);
        const na = isNaN(av) ? -Infinity : av;
        const nb = isNaN(bv) ? -Infinity : bv;
        return sortDir === "asc" ? na - nb : nb - na;
      });
    }
    return arr;
  }, [active.data, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };
  const caret = (key) => (sortKey === key ? (sortDir === "asc" ? "↑" : "↓") : "");

  return (
    <div className="w-full">
      <h4 className="text-lg font-semibold mb-3 mt-3">Trade Deals</h4>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b mb-3 pb-2">
        {TAB_CONFIG.map(({ key, label }) => {
          const isActive = key === activeKey;
          return (
            <button
              key={key}
              onClick={() => setActiveKey(key)}
              className={`px-3 py-1 text-sm rounded-t border-b-2 transition ${
                isActive
                  ? "border-blue-600 text-blue-700 font-semibold"
                  : "border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-300"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {active.loading ? (
        <div className="flex items-center gap-2 text-blue-700">
          <span className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : active.error ? (
        <div className="text-red-600 text-sm">No data available</div>
      ) : active.data.length === 0 ? (
        <div className="text-gray-500 text-sm">No records found.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr className="font-semibold">
                <th
                  className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap cursor-pointer select-none"
                  onClick={() => toggleSort("date")}
                  title="Sort by Date"
                >
                  Date {caret("date")}
                </th>
                <th className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap">Buyer/Seller</th>
                <th className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap">Type</th>
                <th className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap">Mode</th>
                <th className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap">Qty</th>
                <th className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap">Avg/WATP</th>
                <th
                  className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap cursor-pointer select-none"
                  onClick={() => toggleSort("d1")}
                  title="Sort by 1D"
                >
                  1D {caret("d1")}
                </th>
                <th
                  className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap cursor-pointer select-none"
                  onClick={() => toggleSort("w1")}
                  title="Sort by 1W"
                >
                  1W {caret("w1")}
                </th>
              </tr>
            </thead>

            <tbody className="text-gray-700">
              {sortedRows.map((row, idx) => {
                const d1 = getD1(row);
                const w1 = getW1(row);
                const qty = getQty(row);
                const avg = getAvg(row);
                const dealCr = getDealCr(row);

                return (
                  <tr
                    key={row.hash ?? `${row.symbol}-${row.date}-${idx}`}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-3 py-2 border border-gray-200 align-top">{fmtDate(row.date)}</td>
                    <td className="px-3 py-2 border border-gray-200 align-top">{getClient(row)}</td>
                    <td className="px-3 py-2 border border-gray-200 align-top">
                      <span className={
                        (row.buySell || row.acqSaleType || "").toUpperCase() === "BUY" ? "text-green-700" :
                        (row.buySell || row.acqSaleType || "").toUpperCase() === "SELL" ? "text-red-700" :
                        "text-gray-700"
                      }>
                        {getSide(row)}
                      </span>
                    </td>
                    <td className="px-3 py-2 border border-gray-200 align-top">{getMode(row)}</td>
                    <td className="px-3 py-2 border border-gray-200 align-top tabular-nums">{fmtNum(qty)}</td>
                    <td className="px-3 py-2 border border-gray-200 align-top tabular-nums">{fmtPrice(avg)}</td>

                    <td className={`px-3 py-2 border border-gray-200 align-top tabular-nums ${pctCls(d1)}`}>
                      {pctTxt(d1)}
                    </td>
                    <td className={`px-3 py-2 border border-gray-200 align-top tabular-nums ${pctCls(w1)}`}>
                      {pctTxt(w1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StockHeader = ({
  stockInfo,
  symbol,
  tickPrefix = "₹",
  decimalPoints = 2,
}) => {
  const { isMobile } = useDevice();
  if (!stockInfo) return null;

  const renderPriceChange = () => {
    if (!stockInfo.currentPrice || !stockInfo.previousClose) return null;

    const curr = parseFloat(stockInfo.currentPrice);
    const prev = parseFloat(stockInfo.previousClose);

    if (isNaN(curr) || isNaN(prev) || prev === 0) return null;

    const percentChange = ((curr - prev) / prev) * 100;
    const isPositive = percentChange >= 0;
    const changeColor = isPositive ? "text-green-600" : "text-red-600";

    return (
      <span className={`text-sm font-semibold ${changeColor}`}>
        {`${isPositive ? "+" : ""}${percentChange.toFixed(2)}%`}
      </span>
    );
  };

  const renderSummary = () => {
    const summary = stockInfo.longBusinessSummary;
    if (!summary) return null;

    const first300 = summary.slice(0, 300);
    const lastPeriodIdx = first300.lastIndexOf(".");

    if (lastPeriodIdx !== -1) {
      return first300.slice(0, lastPeriodIdx + 1);
    }

    const firstSentenceEnd = summary.indexOf(".");
    return firstSentenceEnd !== -1
      ? summary.slice(0, firstSentenceEnd + 1)
      : summary;
  };

  const isValidNumber = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) && n !== 0;
  };

  const fmtNum = (v, digits = 2) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n.toFixed(digits) : "—";
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-4">
        {/* Left Column — 1/4 width */}
        <div className="flex-[1]">
          {/* Row 1: Name, Price, % Change */}
          <div className="flex flex-col">
            <h3 className="text-2xl font-bold">
            { stockInfo.quoteType === "SECTOR" ? formatIndustryName(stockInfo.longName)  : stockInfo.longName}
            </h3>
            <div className="text-base text-gray-500">{symbol}</div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1 mt-1">
            {/* Price */}
            {(() => {
              const priceNum = parseFloat(stockInfo.currentPrice);
              if (isNaN(priceNum)) return <span className="text-base">—</span>;

              return (
                <span className="text-base font-medium">
                  {tickPrefix}
                  {priceNum.toFixed(decimalPoints)}
                </span>
              );
            })()}

            {/* % Change */}
            {renderPriceChange()}

            {/* Last update date */}
            {stockInfo.lastUpdateDate && (
              <span className="text-gray-500 text-xs">
                {stockInfo.lastUpdateDate}
              </span>
            )}

            {/* <span className="leading-tight">
              <span className="text-gray-500 text-xs sm:text-sm">NSE:</span>{" "}
            </span> */}
          </div>

          {/* Row: Fut OI + Options PCR (only if both valid) */}
          {stockInfo.quoteType === "EQUITY" &&
            isValidNumber(stockInfo.OI) &&
            isValidNumber(stockInfo.PCR_OI) && (
              <div className="text-sm text-gray-800 flex flex-wrap gap-x-6 gap-y-2 mb-1">
                <span>
                  <span className="text-gray-500">Fut OI:</span>{" "}
                  {fmtNum(stockInfo.OI, 2)}%
                </span>

                <span>
                  <span className="text-gray-500">Op PCR:</span>{" "}
                  {fmtNum(stockInfo.PCR_OI, 2)}
                </span>

                <span>
                  <span className="text-gray-500">Del:</span>{" "}
                  {fmtNum(stockInfo.Delivery_pc, 2)}%
                </span>
              </div>
          )}


          {/* Row 2: Market Cap, ROE, PE */}
          {stockInfo.quoteType === "EQUITY" && (
            <div className="text-sm text-gray-800 flex flex-wrap gap-x-6 gap-y-2 mb-1">
              <span>
                <span className="text-gray-500">M Cap:</span>{" "}
                {isNaN(parseFloat(stockInfo.marketCap))
                  ? "—"
                  : `₹${new Intl.NumberFormat("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(parseFloat(stockInfo.marketCap) / 1e7)} Cr`}
              </span>

              <span>
                <span className="text-gray-500">PE:</span>{" "}
                {isNaN(parseFloat(stockInfo.trailingPE))
                  ? "—"
                  : parseFloat(stockInfo.trailingPE).toFixed(2)}
              </span>

              <span>
                <span className="text-gray-500">ROE:</span>{" "}
                {stockInfo.returnOnEquity
                  ? `${(stockInfo.returnOnEquity * 100).toFixed(2)}%`
                  : "—"}
              </span>
            </div>
          )}

          {/* Row 3: Website Link */}
          {stockInfo.quoteType === "EQUITY" && (
            <div className="text-base text-gray-800 flex flex-wrap gap-3 mb-1">
              {stockInfo.website && (
                <div className="flex items-center gap-1">
                  <span className="text-gray-600">🔗</span>
                  <a
                    href={
                      stockInfo.website.startsWith("http")
                        ? stockInfo.website
                        : `https://${stockInfo.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all"
                  >
                    {stockInfo.website.replace(/^https?:\/\//, "")}
                  </a>
                </div>
              )}

              <div className="flex items-center gap-1">
                <span className="text-gray-600">🔗</span>
                <a
                  href={`https://www.screener.in/company/${symbol}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline break-all"
                >
                  screener.in
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Right Column — 3/4 width */}
        {stockInfo.longBusinessSummary && (
          <div className="flex-[1]">
            <div className="mb-1 flex items-baseline">
              <h4 className="text-base font-semibold">About</h4>
                {!["N", "NEW"].includes(stockInfo.tjiIndustry?.trim().toUpperCase()) && (
                  <span className="ml-2 text-sm text-gray-800 font-normal">
                    [Sector:{" "}
                    {stockInfo.tjiIndustry?.replace(
                      /([a-z])([A-Z])/g,
                      "$1 $2"
                    )}
                    ]
                  </span>
                )}
            </div>
            <p className="text-sm text-gray-500 leading-relaxed">
              {renderSummary()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}


const PCT_FIELDS = ["vs52WH", "1D", "1W", "1M", "3M", "6M", "1Y"];

const toPctNum = (v) => {
  // Accepts "3.67%", "-2.1%", "N/A", null, undefined
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s.toUpperCase() === "N/A" || s === "-") return null;
  const n = Number(s.replace("%", ""));
  return Number.isFinite(n) ? n : null;
};

const pctClass = (n) => {
  if (n == null) return "text-gray-500";
  return n >= 0 ? "text-green-600" : "text-red-600";
};

const pctText = (n, original) => {
  if (n == null) return original && String(original).trim() ? String(original) : "—";
  return `${n.toFixed(2)}%`;
};

const SectorInfoTable = ({ sectorResp, isMobile }) => {
  // sectorResp = { ok:true, key:"MedicalEquipment", data:{ stocks:[...], ... } }

  const [sortConfig, setSortConfig] = useState({ key: "1D", dir: "desc" });

  const rows = Array.isArray(sectorResp?.data?.stocks) ? sectorResp.data.stocks : [];

  const COL_W = { idx: 50, trend: 70 };
  const LEFT_TREND = COL_W.idx;                 // 50
  const LEFT_NAME = COL_W.idx + COL_W.trend;    // 120

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      // default directions
      const defaultDir = key === "name" ? "asc" : "desc";
      return { key, dir: defaultDir };
    });
  };

  const caret = (key) => (sortConfig.key === key ? (sortConfig.dir === "asc" ? "↑" : "↓") : "");

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return isNaN(date) ? 'Invalid date' : date.toLocaleDateString('en-IN');
  };

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const { key, dir } = sortConfig;
    const sign = dir === "asc" ? 1 : -1;

    // Missing always last
    const missingLast = (aMissing, bMissing) => {
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      return null;
    };

    arr.sort((a, b) => {
      if (key === "name") {
        const av = (a?.name || a?.symbol || "").toString().trim();
        const bv = (b?.name || b?.symbol || "").toString().trim();
        const aMissing = !av;
        const bMissing = !bv;

        const miss = missingLast(aMissing, bMissing);
        if (miss != null) return miss;

        return sign * av.localeCompare(bv, "en", { sensitivity: "base" });
      }

      // % fields live in dummyData
      const av = toPctNum(a?.dummyData?.[key]);
      const bv = toPctNum(b?.dummyData?.[key]);

      const miss = missingLast(av == null, bv == null);
      if (miss != null) return miss;

      return sign * (av - bv);
    });

    return arr;
  }, [rows, sortConfig]);

  return (
    <div className="w-full mt-4">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="text-lg font-semibold">
          {formatIndustryName(sectorResp?.key)}{" "}
          <span className="text-sm font-normal text-gray-500">
            ({sortedRows.length} stocks)
          </span>
        </h3>
      </div>

      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-gray-700">
              <th className="sticky left-0 top-0 z-40 bg-gray-50 px-3 py-2 text-left"
                  style={{ width: COL_W.idx, minWidth: COL_W.idx }}>
                #
              </th>

              <th className="sticky top-0 z-40 bg-gray-50 p-2 text-left"
                  style={{ left: LEFT_TREND, width: COL_W.trend, minWidth: COL_W.trend, maxWidth: COL_W.trend }}>
                Trend
              </th>

              <th
                className={`sticky top-0 z-40 bg-gray-50 p-2 text-left cursor-pointer select-none
                  ${isMobile ? "min-w-[200px] max-w-[200px]" : "min-w-[250px] max-w-[250px]"}
                `}
                style={{ left: LEFT_NAME }}
                onClick={() => toggleSort("name")}
              >
                Name {caret("name")}
              </th>

              {PCT_FIELDS.map((f) => (
                <th
                  key={f}
                  className="sticky top-0 z-20 bg-gray-50 px-3 py-2 text-right cursor-pointer select-none whitespace-nowrap"
                  onClick={() => toggleSort(f)}
                  title={`Sort by ${f}`}
                >
                  {f} {caret(f)}
                </th>
              ))}
            </tr>
          </thead>


          <tbody className="divide-y">
            {sortedRows.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan={3 + PCT_FIELDS.length}>
                  No data.
                </td>
              </tr>
            ) : (
              sortedRows.map((r, idx) => {
                const nm = r?.name || r?.symbol || "—";
                const sym = r?.symbol || "";

                return (
                  <tr key={sym || idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td
                      className="sticky left-0 z-30 bg-blue-50 px-3 py-2 text-left tabular-nums text-blue-600"
                      style={{ width: COL_W.idx, minWidth: COL_W.idx }}
                    >
                      {idx + 1}
                    </td>

                    <td
                      className="sticky z-30 bg-blue-50 p-2 text-blue-600"
                      style={{ left: LEFT_TREND, width: COL_W.trend, minWidth: COL_W.trend, maxWidth: COL_W.trend }}
                    >
                      <Sparklines data={r?.sparklineData || []} height={20} width={100}>
                        <SparklinesLine color="blue" style={{ strokeWidth: 2, fill: "none" }} />
                      </Sparklines>
                    </td>

                    <td
                      className={`sticky bg-blue-50 z-30 p-2 text-blue-400 ${
                        isMobile ? "min-w-[200px] max-w-[200px]" : "min-w-[200px] max-w-[200px]"
                      }`}
                      style={{ left: LEFT_NAME }}
                    >
                      <a
                        href={`/symbol/${r.symbol}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        {r.name}
                      </a>
                        {" "} 
                        {
                        `(CMP ₹${(r.price ?? 0).toFixed(2)})`
                        }                             
                        <>
                        <br />
                          {"PE " + (r.pe ?? 0).toFixed(2) +
                          " | ROE " + ((r.roe ?? 0) * 100).toFixed(2) + "%" +
                          " | Mcap " +  `₹${new Intl.NumberFormat("en-IN", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(parseFloat(r.marketCap) / 1e7)} Cr`}
                        <br />
                        {Array.isArray(r.events) && r.events.length > 0 && (
                          <div className="text-gray-500 text-sm mt-1">
                            {r.events.map((e, idx) => (
                              <div key={idx}>📌 {e.purpose} - {formatDate(e.date)}</div>
                            ))}
                          </div>
                        )}
                          </>
                    </td>

                    {PCT_FIELDS.map((f) => {
                      const raw = r?.dummyData?.[f];
                      const n = toPctNum(raw);
                      return (
                        <td key={f} className={`px-3 py-2 text-right bg-blue-50 tabular-nums ${pctClass(n)}`}>
                          {pctText(n, raw)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function SymbolPage() {
  const [candles, setCandles] = useState({ close: [], volume: [] });
  const [loading, setLoading] = useState(true);
  const [stockInfo, setStockInfo] = useState(null);
  const [tickPrefix, setTickPrefix] = useState("");
  const [selectedRange, setSelectedRange] = useState('1Y');
  const [consolidatedData, setConsolidatedData] = useState([]);
  const [standaloneData, setStandaloneData] = useState([]);
  const [newsData, setNewsData] = useState([]);
  const [newsFeedData, setNewsFeedData] = useState([]);
  const [stockEventsData, setStockEventsData] = useState([]);
  const [stockAnnouncementsData, setStockAnnouncementsData] = useState([]);
  const [sectorInfoData, setSectorInfoData] = useState([]);

  const [activeResultsTab, setActiveResultsTab] = useState('consolidated');
  const [clickedMsg, setClickedMsg] = useState(null);
  const [activeDotKey, setActiveDotKey] = useState(null);
  const { isMobile } = useDevice();
  const { symbol } = useParams();
  let decimalPoints = 2;
  let isSector = false;

  if(symbol.endsWith('USD') && symbol.length === 6)
  {
    decimalPoints = 4;
  }

  if(symbol.endsWith('_SECTOR'))
  {
    isSector = true;
  }

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/candles/${symbol}`)
      .then(res => {
        setCandles(res.data);
      })
      .catch(error => {
        console.error('Error fetching data', error);
      });

      if (symbol) {
        document.title = `${symbol} | Scan360`;
      }
      return () => {
        document.title = "Scan360"; // Optional cleanup
      };
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/info/${symbol}`)
      .then(res => {
        setStockInfo(res.data)
        setLoading(false);
        const quoteType = res.data.quoteType;
        if (quoteType === 'USD') {
          setTickPrefix("$");
        } else if (quoteType === 'WORLD_INDEX' || quoteType === 'SECTOR' ) {
          setTickPrefix("");
        } else {
          setTickPrefix("₹");
        }
      })
      .catch(err => {
        console.error('Error fetching stock info:', err);
        setStockInfo(null);
        setLoading(false);
      });

  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/results/consolidated/${symbol}`)
      .then(res => {
        const sortedData = res.data.sort((a, b) => new Date(a.toDate) - new Date(b.toDate));
        setConsolidatedData(sortedData);
      })
      .catch(err => {
        console.error('Error fetching consolidated data:', err);
        setConsolidatedData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/results/standalone/${symbol}`)
      .then(res => {
        const sortedData = res.data.sort((a, b) => new Date(a.toDate) - new Date(b.toDate));
        setStandaloneData(sortedData);
      })
      .catch(err => {
        console.error('Error fetching consolidated data:', err);
        setStandaloneData([]);
      });
  }, [symbol]);

  useEffect(() => {
    if (consolidatedData.length === 0 && standaloneData.length > 0) {
      setActiveResultsTab('standalone');
    }
  }, [consolidatedData, standaloneData]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/news/${symbol}`)
      .then(res => {
        setNewsData(res.data);
      })
      .catch(err => {
        console.error('Error fetching consolidated data:', err);
        setNewsData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/news_feed/${symbol}`)
      .then(res => {
        setNewsFeedData(res.data);
      })
      .catch(err => {
        console.error('Error fetching news feed data:', err);
        setNewsFeedData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api_2/stockEvents/${symbol}`)
      .then(res => {
        setStockEventsData(res.data);
      })
      .catch(err => {
        console.error('Error fetching news feed data:', err);
        setStockEventsData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api_2/stockAnnouncements/${symbol}`)
      .then(res => {
        setStockAnnouncementsData(res.data);
      })
      .catch(err => {
        console.error('Error fetching news feed data:', err);
        setStockAnnouncementsData([]);
      });
  }, [symbol]);

  useEffect(() => {
    if(isSector)
    {
      axios.get(`${process.env.REACT_APP_API_URL}/api/sector_info/${symbol}`)
        .then(res => {
          setSectorInfoData(res.data);
        })
        .catch(err => {
          console.error('Error fetching news feed data:', err);
          setSectorInfoData([]);
        });
    }
  }, [symbol]);


  // build a unique key for each event (adjust if your data shape differs)
  const eventKey = useCallback((ev) => {
    return `${ev.date}__${ev.type || ""}__${ev.url || ""}`;
  }, []);

  // Utility function to generate N ticks between min and max
  const generateTicks = (min, max, count, toFixed = null) => {
    if (min === max) {
      // Prevent division by zero; add ±1 step around flat value
      return Array.from({ length: count }, (_, i) => min + (i - Math.floor(count / 2)));
    }

    const step = (max - min) / (count - 1);
    return Array.from({ length: count }, (_, i) => {
      const val = min + i * step;
      return toFixed !== null ? Number(val.toFixed(toFixed)) : Math.round(val);
    });
  };

  // Normalize any incoming date to "YYYY-MM-DD"
  const toYMD = (d) => {
    if (!d) return d;
    // Handle "DD-MM-YYYY"
    if (/^\d{2}-\d{2}-\d{4}$/.test(d)) {
      const [DD, MM, YYYY] = d.split('-');
      return `${YYYY}-${MM}-${DD}`;
    }
    // Handle ISO like "2025-08-01T00:00:00Z"
    const dt = new Date(d);
    if (!isNaN(dt)) {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    return d; // already Y-M-D string
  };

  const getChartData = () => {
    const totalLength = candles.close.length;
    const limit = timeFrames[selectedRange];
    const start = Math.max(0, totalLength - limit);

    const hasTrades = Array.isArray(candles.trades);
    const hasOI = Array.isArray(candles.OI);

    // ✅ NEW: Options OI arrays
    const hasCEOI = Array.isArray(candles.CE_OI);
    const hasPEOI = Array.isArray(candles.PE_OI);

    return candles.close.slice(start).map((close, i) => {
      const idx = start + i;

      const volume = Number(candles.volume?.[idx] ?? 0);

      const deliveryQtyRaw = Array.isArray(candles.delivery)
        ? candles.delivery[idx]
        : null;
      const deliveryQty = deliveryQtyRaw == null ? null : Number(deliveryQtyRaw);

      const hasDeliveryThisRow =
        Number.isFinite(deliveryQty) && Number.isFinite(volume) && volume > 0;

      const deliveredVol = hasDeliveryThisRow
        ? Math.max(0, Math.min(volume, deliveryQty))
        : 0;

      const undeliveredVol = hasDeliveryThisRow
        ? Math.max(0, volume - deliveredVol)
        : volume;

      const tradesRaw = hasTrades ? candles.trades[idx] : null;
      const trades = tradesRaw == null ? null : Number(tradesRaw);

      const ats =
        trades != null && trades > 0 && Number.isFinite(volume)
          ? volume / trades
          : null;

      // ✅ OI% (from backend) — allow null/undefined
      const oiRaw = hasOI ? candles.OI?.[idx] : null;
      const oi = oiRaw == null ? null : Number(oiRaw);
      const oiPct = Number.isFinite(oi) ? oi : null;

      // ✅ NEW: CE/PE OI + client-side PCR_OI
      const ceRaw = hasCEOI ? candles.CE_OI?.[idx] : null;
      const peRaw = hasPEOI ? candles.PE_OI?.[idx] : null;

      const ceOi = ceRaw == null ? null : Number(ceRaw);
      const peOi = peRaw == null ? null : Number(peRaw);

      const ceOiSafe = Number.isFinite(ceOi) ? ceOi : null;
      const peOiSafe = Number.isFinite(peOi) ? peOi : null;

      // original pcr meaning if > 1 PE more, if < 1, CE more
      const pcrOi =
        ceOiSafe != null && ceOiSafe > 0 && peOiSafe != null
          ? (peOiSafe / ceOiSafe) - 1   // ✅ normalize around 1
          : null;

    return {
      date: toYMD(candles.date?.[idx]),
      close: Number(close),
      volume,
      delivery: deliveryQty,
      deliveredVol,
      undeliveredVol,
      trades,
      ats,
      oi: oiPct,
      ceOi: ceOiSafe,
      peOi: peOiSafe,
      pcrOi,   // PCR normalized around 1
    };
    });
  };


  // if date fall on saturday or sunday, if will fall to monday
  const getCloseByDate = (data, dateStr) => {
    if (!data?.length) return null;

    const toYMD = (d) => {
      const dt = new Date(d);
      if (isNaN(dt)) return null;
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const target = toYMD(dateStr);
    if (!target) return null;

    // exact match
    const exact = data.find(d => d.date === target);
    if (exact) return { date: exact.date, close: exact.close };

    // next available trading day
    const targetTs = new Date(target).getTime();
    const future = data.find(d => new Date(d.date).getTime() > targetTs);
    return future ? { date: future.date, close: future.close } : null;
  };


  // 1) chartData memo
  const chartData = useMemo(() => getChartData(), [candles, selectedRange]);

  // 2) derived ranges memo
  const closePrices = useMemo(() => chartData.map(d => d.close), [chartData]);
  const yMin = useMemo(() => Math.min(...closePrices), [closePrices]);
  const yMax = useMemo(() => Math.max(...closePrices), [closePrices]);

  const volumeData = useMemo(() => chartData.map(d => d.volume), [chartData]);
  const vMin = useMemo(() => Math.min(...volumeData), [volumeData]);
  const vMax = useMemo(() => Math.max(...volumeData), [volumeData]);

  // 3) ticks memo
  const priceTicks = useMemo(
    () => generateTicks(yMin, yMax, 8, decimalPoints),
    [yMin, yMax, decimalPoints]
  );
  const volumeTicks = useMemo(
    () => generateTicks(vMin, vMax, 8),
    [vMin, vMax]
  );
  const xTicks = useMemo(() => {
    const xTickCount = 6;
    const interval = Math.max(1, Math.floor(chartData.length / (xTickCount - 1)));
    const ticks = chartData.filter((_, i) => i % interval === 0).map(d => d.date);
    if (chartData.length > 0 && ticks[ticks.length - 1] !== chartData[chartData.length - 1].date) {
      ticks.push(chartData[chartData.length - 1].date);
    }
    return ticks;
  }, [chartData]);

  // margin={{ top: 6, right: 8, left: 8, bottom: 0 }}
  // 4) stable margin + click handler
  const chartMargin = useMemo(
    () => ({ top: 6, right: isMobile ? 10 : 20, left: isMobile ? 10 : 20, bottom: 0 }),
    [isMobile]
  );

  // Avoid setting state if the same event is clicked again (prevents useless parent re-renders)
  const handleDotClick = useCallback((msg) => {
    setClickedMsg(prev =>
      prev &&
      prev.type === msg.type &&
      prev.date === msg.date &&
      prev.price === msg.price &&
      prev.url === msg.url &&
      prev.text === msg.text
        ? prev
        : msg
    );
    // mark the dot active
    setActiveDotKey(eventKey(msg)); // msg has date/type/url
  }, [eventKey]);

  if (loading) return <div className="p-4">Loading...</div>;
  // if (!candles.close || candles.close.length === 0) return <div className="p-4">No data available</div>;

  return (
    <div className="p-4 mb-10 overscroll-y-contain overflow-x-hidden touch-pan-y">
      <StockHeader
        stockInfo={stockInfo}
        symbol={symbol}
        tickPrefix={tickPrefix}
        decimalPoints={decimalPoints}
      />

      {/* Timeline Buttons */}
      <div className="mt-1 mb-1 inline-flex flex-wrap gap-0 border border-gray-300 rounded-md overflow-hidden">
        {Object.keys(timeFrames).map((range, idx, arr) => {
          const isActive = selectedRange === range;
          const isFirst = idx === 0;
          const isLast = idx === arr.length - 1;

          return (
            <button
              key={range}
              onClick={() => setSelectedRange(range)}
              className={`transition-all duration-200 font-medium
                ${isActive
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'}
                ${!isLast ? 'border-r border-gray-300' : ''}
                ${isFirst ? 'rounded-l-md' : ''}
                ${isLast ? 'rounded-r-md' : ''}
                px-3 py-1 text-sm sm:px-3.5 sm:py-1 sm:text-sm
              `}
            >
              {range}
            </button>
          );
        })}
      </div>

      {/* Sticky message box above the chart */}
      <div className="mb-2">   {/* 👈 reserve ~80px */}
        {clickedMsg ? (
          <div className="w-full max-w-full md:max-w-3xl px-3 py-2 border rounded-lg bg-white shadow-sm text-sm">
            {/* Row 1: type + meta */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
              {/* Type chip */}
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                {clickedMsg.type}
              </span>

              {/* Date */}
              <span className="font-mono text-gray-700">
                {clickedMsg.date}
              </span>

              {/* Price */}
              <span className="font-mono text-gray-900">
                {tickPrefix}{Number(clickedMsg.price).toFixed(decimalPoints)}
              </span>
            </div>

            {/* Row 2: details (wraps + scrolls if very long) */}
            <div>
              <span className="text-gray-700 whitespace-pre-wrap break-words max-h-28 overflow-auto">
                {clickedMsg.text}
              </span>

              {/* Row 3: link (breaks on small screens) */}
              <span className="ml-1">
              {clickedMsg.url && (
                <a
                  href={clickedMsg.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-block text-blue-600 underline break-all"
                >
                  source
                </a>
              )}
              </span>
            </div>
          </div>
        ) : (
          <div className="inline-flex items-start gap-2 sm:gap-3 px-3 py-2 border rounded bg-white shadow-sm text-sm">
            💡 Tap a marker on the chart to show event details.
          </div>
        )}
      </div>

      {/* Stock Chart */}
      <PriceChart
        chartData={chartData}
        stockInfo={stockInfo}
        isMobile={isMobile}
        margin={chartMargin}
        xTicks={xTicks}
        volumeTicks={volumeTicks}
        priceTicks={priceTicks}
        yMin={yMin}
        yMax={yMax}
        decimalPoints={decimalPoints}
        tickPrefix={tickPrefix}
        newsData={newsData}
        onDotClick={handleDotClick}
        getCloseByDate={getCloseByDate}
        activeDotKey={activeDotKey}   // 👈
        eventKey={eventKey}           // 👈
      />

      {/* Financial Impacts */}
      {stockInfo && <FinancialImpacts stockInfo={stockInfo} />}

      {stockInfo && stockInfo.quoteType === "SECTOR" && (
      <SectorInfoTable sectorResp={sectorInfoData} isMobile={isMobile} />
      )}

      {/* News & Stories */}
      <NewsFeedTable data={newsFeedData} />

      {stockInfo && stockInfo.quoteType === "EQUITY" && (
        <>
          {/* Financial Results */}
          <div>
            <h4 className="text-lg font-semibold mt-6">
              Financial Results
              <span className="text-sm text-gray-400 font-normal ml-2">(Rs. Crores)</span>
            </h4>
            {/* Tab buttons */}
            {consolidatedData.length > 0 && standaloneData.length > 0 && (
              <div className="my-2 flex gap-2">
                <button
                  onClick={() => setActiveResultsTab('consolidated')}
                  className={`px-3 py-1 text-sm rounded border ${activeResultsTab === 'consolidated' ? 'bg-blue-200 font-semibold' : 'bg-gray-100'}`}
                >
                  Consolidated
                </button>
                <button
                  onClick={() => setActiveResultsTab('standalone')}
                  className={`px-3 py-1 text-sm rounded border ${activeResultsTab === 'standalone' ? 'bg-blue-200 font-semibold' : 'bg-gray-100'}`}
                >
                  Standalone
                </button>
              </div>
            )}
          </div>

          {(consolidatedData.length > 0 || standaloneData.length > 0) ? (
            <div className="mt-2 overflow-x-auto rounded-lg border border-gray-300 mb-4">
              {/* Table */}
              <table className="min-w-full border text-sm text-left">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border px-2 py-1 sticky left-0 bg-gray-100 z-10"></th>
                    {(activeResultsTab === 'consolidated' ? consolidatedData : standaloneData).map((row, idx) => (
                      <th
                        key={idx}
                        className="border px-2 py-1 whitespace-nowrap text-right"
                      >
                        <div>{row.toDate}</div>
                        {row.broadCastDate && (
                          <div className="text-[12px] font-light text-gray-500">
                            {new Date(row.broadCastDate).toLocaleDateString("en-GB")}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    "Sales", "OperatingProfit", "OPM", "OtherIncome",
                    "Interest", "Depreciation", "ProfitBeforeTax",
                    "NetProfit", "EPS"
                  ].map((metric, rowIdx) => {
                    const isEven = rowIdx % 2 === 0;
                    const rowBg = isEven ? 'bg-white' : 'bg-gray-200';

                    return (
                      <tr key={metric} className={rowBg}>
                        <td
                          className={`border px-2 py-1 font-medium sticky left-0 z-10 ${rowBg}`}
                        >
                          {metric}
                        </td>
                        {(activeResultsTab === 'consolidated' ? consolidatedData : standaloneData).map((row, idx) => {
                          const value = row[metric];
                          const formattedValue =
                            value !== undefined
                              ? metric === "OPM"
                                ? `${parseFloat(value).toFixed(1)}%`
                                : parseFloat(value).toLocaleString()
                              : '—';

                          return (
                            <td key={idx} className="border px-2 py-1 text-right">
                              {formattedValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-6 text-gray-500 italic text-sm mb-4">
              No financial data available
            </div>
          )}

          <StockEvents data={stockEventsData} />
          <StockAnnouncements data={stockAnnouncementsData} />
          <StockTrades symbol={symbol} />
        </>
      )}

    </div>
  );
}

export default SymbolPage;
